import React, { useEffect, useState } from 'react';
import { Input, Select, Button, Radio } from 'antd/lib';
import { map, compose, propOr, defaultTo, find, propEq, prop, cond, always, T, uniqBy, filter, allPass, not, applySpec, assocPath, dissocPath, nth, drop, assoc, over, lensProp, append, when, flip, includes, and, isNotNil, isNotEmpty } from 'ramda';
import { mapIndexed } from 'ramda-adjunct';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { Components } from '@fasstech/flexiget';
import { usePasteGuard } from '../../../../hooks';
import conditions from './conditions';
import conditionsPredefined from './conditionsPredefined';
import actions from './actions';
import operators from './operators';

const conditionIsWithValue = condition => compose(
  propOr(false, 'withValue'),
  defaultTo({}),
  find(propEq(condition, 'key'))
)(conditions);

const CustomSelect = ({
  placeholder,
  value,
  onChange,
  options = [],
  disabled
}) => {
  const { searchValue, open, handleSearch, handleKeyDown, handlePaste, handleDropdownVisibleChange, resetOnSelect } = usePasteGuard();

  const handleChange = (nextValue) => {
    resetOnSelect();
    onChange(nextValue);
  };

  return (
    <div className="w-3/4" onPaste={handlePaste} onKeyDown={handleKeyDown}>
      <Select
        showSearch
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        searchValue={searchValue}
        onSearch={handleSearch}
        optionFilterProp="children"
        size="small"
        className="w-full"
        disabled={disabled}
        open={open}
        onDropdownVisibleChange={handleDropdownVisibleChange}
      >
        {map(({ key, label }) => {
          return (
            <Select.Option
              key={key}
              value={key}
            >
              {label}
            </Select.Option>
          );
        })(options)}
      </Select>
    </div>
  );
};

const DisplayRadio = ({ value }) => map(({ key, label }) => {
  return (
    <Radio
      key={key}
      value={key}
    >
      <span className="font-semibold">{label}</span>
    </Radio>
  );
})(value);

const Condition = ({
  condition,
  conditionIndex,
  onUpdate,
  variables,
  preview,
  userCanEdit,
  setIsOpenCreateGlobalVariableModal
}) => {
  const [value, setValue] = useState(prop('value', condition));
  useEffect(() => setValue(prop('value', condition)), [condition]);

  const isFirst = conditionIndex === 0;
  const conditionVariable = prop('variable', condition);

  const PREDEFINED_VARIABLES = ['_pageTotal', '_pageCouranteSection', '_pageTotalSection'];
  const isNotPredefinedVariable = compose(
    not,
    flip(includes)(PREDEFINED_VARIABLES),
    prop('key')
  );
  const isPageCouranteText = variable => propEq('_pageCourante', 'key', variable) && find(
    allPass([
      propEq('_pageCourante', 'key'),
      propEq('rule', 'type')
    ]),
    variables
  ) && !propEq('predefined', 'type', variable);
  const variablesWithoutPredefined = filter(
    allPass([
      isNotPredefinedVariable,
      variable => !isPageCouranteText(variable)
    ])
  )(variables);
  const variable = find(propEq(conditionVariable, 'key'), variablesWithoutPredefined);

  const generateConditionsOptions = (variableKey, variable) => when(
    () => and(isNotNil(variableKey), isNotNil(variable)),
    cond([
      [propEq('predefined', 'type'), always(conditionsPredefined)],
      [propEq('number', 'type'), always(conditions)],
      [T, always(filter(
        allPass([
          compose(
            not,
            propEq('isGreaterThan', 'key')
          ),
          compose(
            not,
            propEq('isLessThan', 'key')
          )
        ]),
        conditions
      ))]
    ])
  )(variable);

  const optionsWithoutDuplicateKeys = compose(
    uniqBy(prop('key')),
    map(
      applySpec({
        key: prop('key'),
        label: prop('key')
      })
    )
  )(variablesWithoutPredefined);
  const disableFields = preview || !userCanEdit;

  const onChangeVariable = (variable) => {
    const mutationsFn = [assocPath(['conditions', conditionIndex, 'variable'], variable)];
    const newVariable = find(propEq(variable, 'key'), variablesWithoutPredefined);
    const newConditionsOptions = generateConditionsOptions(variable, newVariable);
    if (!find(propEq(prop('condition', condition), 'key'), newConditionsOptions)) {
      mutationsFn.unshift(assocPath(['conditions', conditionIndex, 'condition'], null));
    }
    onUpdate(compose(...mutationsFn));
  };

  return (
    <>
      {!isFirst && (
        <div className="flex items-center justify-between mt-2">
          <Radio.Group
            onChange={e => onUpdate(assocPath(['conditions', conditionIndex, 'operator'], e.target.value))}
            value={prop('operator', condition)}
            className="radio-widget-rule"
            disabled={disableFields}
          >
            <DisplayRadio value={operators} />
          </Radio.Group>
        </div>
      )}
      <div className="flex items-center justify-between my-2">
        {(isFirst || isNotNil(prop('operator', condition))) && (
          <>
            <CustomSelect
              placeholder="Sélectionner une variable"
              value={conditionVariable}
              onChange={onChangeVariable}
              options={optionsWithoutDuplicateKeys}
              disabled={disableFields}
            />
            {userCanEdit && (
              <>
                <Components.Tooltip
                  title="Créer une variable globale"
                >
                  <PlusCircleOutlined
                    className="create-global-variable-button"
                    onClick={() => setIsOpenCreateGlobalVariableModal({ onUpdate, conditionIndex })}
                  />
                </Components.Tooltip>
                <Components.Tooltip
                  title="Supprimer la condition"
                >
                  <button onClick={() => onUpdate(dissocPath(['conditions', conditionIndex]))} disabled={preview} >
                    <DeleteOutlined className="btn-remove-condition hover:text-flexi-red"/>
                  </button>
                </Components.Tooltip>
              </>
            )}
          </>
        )}
      </div>

      {isNotNil(conditionVariable) && (
        <CustomSelect
          placeholder="Sélectionner une condition"
          value={prop('condition', condition)}
          onChange={v => onUpdate(assocPath(['conditions', conditionIndex, 'condition'], v))}
          options={generateConditionsOptions(conditionVariable, variable)}
          disabled={disableFields}
        />
      )}

      {isNotNil(prop('condition', condition)) && conditionIsWithValue(condition.condition) && (
        <Input
          className="!w-3/4 mt-2"
          placeholder="Saisir la valeur"
          value={value}
          onChange={e => setValue(e.target.value)}
          size="small"
          disabled={disableFields}
          onBlur={(e) => onUpdate(assocPath(['conditions', conditionIndex, 'value'], e.target.value))}
        />
      )}
    </>
  );
};

export const Rule = ({
  variables,
  onUpdate,
  onRemove,
  rule,
  ruleIndex,
  getConditionsAreValid,
  preview,
  userCanEdit,
  setIsOpenCreateGlobalVariableModal,
  isWidget
}) => {
  const conditions = propOr([], 'conditions', rule);
  const firstCondition = compose(
    defaultTo({}),
    nth(0)
  )(conditions);
  const extraConditions = drop(1, conditions);
  const canEdit = userCanEdit && !preview;

  return (
    <div className={classNames({ 'max-w-full': isWidget })}>
      <div className="flex items-center justify-between">
        <Radio.Group
          onChange={e => onUpdate(assoc('action', e.target.value))}
          value={prop('action', rule)}
          className="radio-widget-rule"
          disabled={!userCanEdit || preview}
        >
          <DisplayRadio value={actions} />
        </Radio.Group>

        {canEdit && (
          <Components.Tooltip
            title="Supprimer la règle"
          >
            <DeleteOutlined
              className="btn-remove-rule hover:text-flexi-red"
              onClick={onRemove}
            />
          </Components.Tooltip>
        )}
      </div>

      {isNotNil(prop('action', rule)) && (
        <Condition
          conditionIndex={0}
          condition={firstCondition}
          onUpdate={onUpdate}
          variables={variables}
          preview={preview}
          userCanEdit={userCanEdit}
          setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
        />
      )}

      {mapIndexed((condition, conditionIndex) => {
        return (
          <Condition
            key={`rule-${ruleIndex}-condition-${conditionIndex + 1}`}
            conditionIndex={conditionIndex + 1}
            condition={condition}
            onUpdate={onUpdate}
            variables={variables}
            preview={preview}
            userCanEdit={userCanEdit}
            setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
          />
        );
      })(extraConditions)}

      {isNotEmpty(conditions) && getConditionsAreValid(conditions) && canEdit && (
        <Button
          size="small"
          className={classNames('mt-2', { 'small-white-button': not(isWidget) })}
          icon={<PlusCircleOutlined/>}
          onClick={() => onUpdate(over(
            lensProp('conditions'),
            append({})
          ))}
          disabled={!getConditionsAreValid(propOr([], 'conditions', rule))}
        >
          Ajouter une condition
        </Button>
      )}

    </div>
  );
};

const DocumentRules = ({
  variables,
  rules,
  rulesAreValid,
  getConditionsAreValid,
  onAdd,
  onRemove,
  onUpdate,
  preview,
  classNameButton,
  userCanEdit,
  setIsOpenCreateGlobalVariableModal
}) => {
  const hasRightToAddRule = userCanEdit && rulesAreValid && !preview;

  return (
    <div className="w-80">
      {isNotEmpty(rules) && (
        <div className="flex mt-2 mb-2">
          <div className="border-4 border-gray-300 rounded-sm mr-2.5"/>
          <div className="flex flex-col gap-y-4 w-full">
            {mapIndexed((rule, index) => (
              <Rule
                rule={rule}
                variables={variables}
                onUpdate={onUpdate(index)}
                onRemove={onRemove(index)}
                key={`rule${index}`}
                ruleIndex={index}
                getConditionsAreValid={getConditionsAreValid}
                preview={preview}
                userCanEdit={userCanEdit}
                setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
              />
            ))(rules)}
          </div>
        </div>
      )}

      {hasRightToAddRule && (
        <Button
          size="small"
          className={`add-rules-button small-white-button mt-2 ${classNameButton}`}
          disabled={!rulesAreValid}
          onClick={onAdd}
          icon={<PlusCircleOutlined/>}
        >
          Ajouter une règle
        </Button>
      )}
    </div>
  );
};

export default DocumentRules;
